import { useEffect, useRef, type ReactNode } from 'react'
import { AppInstallBanner } from './AppInstallBanner'
import { GuestPicksFooter, useShowGuestPicksFooter } from './GuestPicksFooter'

// Owns the stack at the bottom of the screen so the install banner always sits
// directly above whichever nav is in play — the app-wide one or festival mode's.
// z-[60] is the chrome step of the scale in the Theming section of CLAUDE.md; the
// banner rides along with the nav rather than carrying a z-index of its own.
// The stack is click-through (`chrome-passthrough`) so the clear air around the floating
// pill doesn't swallow taps meant for the page; only the glass surfaces
// (`chrome-hit`) take them. That re-enable is withdrawn while an open Radix
// menu makes <body> inert — otherwise a tap meant to dismiss the festival Profile
// menu landed on the banner behind it and opened the App Store.
// While a guest's picks footer shows, it takes the install banner's slot.
// The nav's compact/expand transition (300ms in NavBar) plus a frame of slack.
const NAV_SETTLE_MS = 350

export function BottomChrome({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const showPicks = useShowGuestPicksFooter()
  // Publishes the stack's live height (banners included) so pages that scroll
  // under the floating nav can pad by exactly what covers them.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement
    // Hold the expanded height while the nav is compacted (useNavCompact) and while it
    // grows back: measured mid-expansion the stack is still short, so everything
    // positioned off this height (Home's FABs) would dip, then rise with the nav.
    let settleUntil = 0
    let settle: ReturnType<typeof setTimeout> | undefined
    const measure = () => {
      if ('navCompact' in root.dataset || performance.now() < settleUntil) return
      root.style.setProperty('--bottom-chrome-h', `${el.offsetHeight}px`)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    let wasCompact = 'navCompact' in root.dataset
    const mo = new MutationObserver(() => {
      const compact = 'navCompact' in root.dataset
      if (wasCompact && !compact) {
        settleUntil = performance.now() + NAV_SETTLE_MS
        clearTimeout(settle)
        settle = setTimeout(measure, NAV_SETTLE_MS)
      }
      wasCompact = compact
    })
    mo.observe(root, { attributes: true, attributeFilter: ['data-nav-compact'] })
    return () => {
      ro.disconnect()
      mo.disconnect()
      clearTimeout(settle)
      root.style.removeProperty('--bottom-chrome-h')
    }
  }, [])
  return (
    <div ref={ref} className="chrome-passthrough fixed bottom-0 left-0 right-0 z-[60] flex flex-col">
      {showPicks ? <GuestPicksFooter /> : <AppInstallBanner />}
      {children}
    </div>
  )
}
